import React, {
  type BaseSyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { graphql, type PageProps } from 'gatsby';
import styled from 'styled-components';
import AdditionalLinks from '@repo/ui-components/collections/AdditionalLinks';
import breakpoints from '@repo/ui-components/breakpoints';
import BlockBuilderLinkGrid from '@repo/ui-components/sanity/types/BlockBuilderLinkGrid/BlockBuilderLinkGrid';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import CollectionHeaderFilters from '@repo/ui-components/collections/HeaderFilters';
import Container from '@repo/ui-components/collections/Container';
import ContentContainer from '@repo/ui-components/collections/ContentContainer';
import ContainerStyles from '@repo/ui-components/collections/ContainerStyles';
import FilterBox from '@repo/ui-components/collections/FilterBox';
import CollectionHeader from '@repo/ui-components/collections/Header';
import Hero from '@repo/ui-components/hero/Hero';
import PaginationArea from '@repo/ui-components/collections/PaginationArea';
import ProductCard from '@repo/ui-components/product-card/ProductCard';
import ProductCardGrid from '@repo/ui-components/ProductCardGrid';
import SelectSearch from '@repo/ui-components/collections/SelectSearch';
import sharedStyles from '@repo/ui-components/styles';
import Sidebar from '@repo/ui-components/collections/Sidebar';
import SidebarRow from '@repo/ui-components/collections/SidebarRow';
import SortByBox from '@repo/ui-components/collections/SortByBox';
import getResizedProductImage from '@repo/utils/getResizedProductImage';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import Layout from '@components/layout';
import GearTagLine from '@components/GearTagLine';
import getInternalLink from '@components/utils/getInternalLink';
import collectionsMapping from '@content/collectionsMapping';
import styles from '@components/utils/styles';
import {
  getHreflangValuesForPaginatedPages,
  getLocalizedPagePath,
  LocalizedLink,
  useLocalizedNavigation,
} from '@repo/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';
import { type SanityGlobalProductCollection } from '@repo/models/gatsby-graphql-types';
import { isPixelSupported, PIXELS } from '@repo/utils/pixels';

const StyledTopBodyContainer = styled.div`
  margin-bottom: ${sharedStyles.spacing['spacing-md']};
  @media screen and (min-width: ${breakpoints.md}) {
    margin-bottom: 0;
  }
`;

const getBreadcrumbs = (
  collection: SanityGlobalProductCollection,
  {
    home,
    partsAndAccessories,
    collectionTitleReplaceValue,
  }: {
    home: string;
    partsAndAccessories: string;
    collectionTitleReplaceValue: string;
  }
) => {
  const breadcrumbs: Array<{ text: string; link?: string }> = [
    { text: home, link: '/' },
  ];

  if (
    collection?.store?.title?.includes(partsAndAccessories) &&
    collection?.store?.slug?.current !== 'accessories'
  ) {
    breadcrumbs.push({
      text: partsAndAccessories,
      link: '/accessories',
    });
  } else if (collection?.parentCollection) {
    const mappedCollection = collectionsMapping.find(
      (mapping: { key: string; value: string }) =>
        mapping.key === collection.parentCollection.store.slug.current
    );
    breadcrumbs.push({
      text: collection.parentCollection.store.title,
      link: mappedCollection
        ? mappedCollection.value
        : `/${collection.parentCollection.store.slug.current}`,
    });
  }

  breadcrumbs.push({
    text: collection?.store?.title?.replace(collectionTitleReplaceValue, ''),
  });

  return breadcrumbs;
};

type PageContextProps = {
  collectionId: string;
  locale: string;
  humanPageNumber: number;
  limit: number;
  skip: number;
  pageNumber: number;
  previousPagePath: string;
  nextPagePath: string;
  sortOrder?: string;
  sortedProductIds: Array<string>;
  linkHreflangValues: Array<{ lang: string; urlPrefix: string }>;
  pageCountByLanguage: { [key: string]: number };
};

type DataProps = {
  collection: SanityGlobalProductCollection;
};

function CollectionTemplate({
  pageContext,
  data,
  location,
}: Readonly<PageProps<DataProps, PageContextProps>>) {
  const { collection } = data;
  const { tt, locale } = useSportdogInternationalContext();
  const navigate = useLocalizedNavigation(locale);
  const pagedAndSortedProductIds = pageContext.sortedProductIds.slice(
    pageContext.skip,
    pageContext.humanPageNumber * pageContext.limit
  );
  const pagedProducts = pagedAndSortedProductIds.map(id =>
    collection.products.find(product => product._id === id)
  );

  useEffect(() => {
    if (isPixelSupported(PIXELS.klaviyo)) {
      window?.klaviyo?.push([
        'track',
        'Viewed Category',
        {
          CategoryName: collection.store.title,
          URL: location.href,
        },
      ]);
    }
  }, [collection.store.title, location.href]);

  const breadcrumbs = getBreadcrumbs(collection, {
    home: tt(s => s.breadcrumbsCommon.home),
    partsAndAccessories: tt(s => s.breadcrumbsCommon.partsAndAccessories),
    collectionTitleReplaceValue: tt(
      s => s.collectionsPage.collectionTitleReplaceValue
    ),
  });

  const [filterValue, setFilterValue] = useState<string>(
    pageContext.sortOrder ?? ''
  );

  const getSelectedOptionValue = (element: HTMLSelectElement) =>
    element.options[element.options.selectedIndex].value;

  const navigateOnChange = (event: BaseSyntheticEvent<HTMLSelectElement>) => {
    const entryUrl = `${getSelectedOptionValue(event.target)}`;
    let collectionUrl: string;

    if (
      entryUrl.startsWith('parts-accessories-') ||
      entryUrl.startsWith('accessories-')
    ) {
      collectionUrl = `/accessories/${entryUrl.split('accessories-')[1]}`;
    } else {
      collectionUrl = `/${collection.store.slug.current}/${entryUrl}`;
    }

    navigate(collectionUrl);
  };

  const handleSortingFilter = (
    event: BaseSyntheticEvent<HTMLSelectElement>
  ) => {
    const filter = getSelectedOptionValue(event.target);
    setFilterValue(filter);

    let navigateUrl = `/${collection.store.slug.current}`;

    if (collection?.parentCollection) {
      if (
        navigateUrl.startsWith('/parts-accessories-') ||
        navigateUrl.startsWith('/accessories-')
      ) {
        navigateUrl = `/accessories/${
          navigateUrl.split('accessories-')[1]
        }/${filter}`;
      } else {
        navigateUrl = `/${collection.parentCollection.store.slug.current}/${collection.store.slug.current}/${filter}`;
      }
    } else {
      navigateUrl = `/${collection.store.slug.current}/${filter}`;
    }

    navigate(navigateUrl);
  };

  const marketingRef = useRef<HTMLDivElement>();
  const sidebarWidthPercent = 20;

  return (
    <Layout
      headTagData={{
        title: collection?.SEOMetadata?.title || collection?.store?.title,
        description:
          collection?.SEOMetadata?.description || collection?.description,
        canonicalUrl: collection?.SEOMetadata?.canonicalUrl,
        noIndex: collection?.SEOMetadata?.noIndex,
        noFollow: collection?.SEOMetadata?.noFollow,
        includeBVScript: true,
        meta: createOpenGraphTags(
          {
            title:
              collection?.SEOMetadata?.ogTitle ||
              collection?.SEOMetadata?.title ||
              collection?.store?.title,
            description:
              collection?.SEOMetadata?.ogDescription ||
              collection?.SEOMetadata?.description ||
              collection?.description,
            url:
              collection?.SEOMetadata?.ogUrl ||
              location.pathname ||
              getLocalizedPagePath('/', locale),
            image:
              collection?.SEOMetadata?.ogImage ||
              collection?.image ||
              pagedProducts?.[0]?.store?.previewImageUrl ||
              '',
          },
          'sportdog'
        ),
      }}
      linkHreflangValues={getHreflangValuesForPaginatedPages({
        values: pageContext.linkHreflangValues,
        humanPageNumber: pageContext.humanPageNumber,
        pageCountByLanguage: pageContext.pageCountByLanguage,
      })}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      <ContainerStyles backgroundColor={styles.colors.gray[900]}>
        {(collection?.hero?.mediaItem || collection?.hero?.heroType) && (
          <Hero hero={collection?.hero} getInternalLink={getInternalLink} />
        )}

        <Container fontColor="white">
          <Sidebar
            active={collection?.sidebar?.length > 0}
            widthPercent={sidebarWidthPercent}
          >
            {collection?.sidebar?.length > 0 && (
              <>
                <h5>{tt(s => s.collectionsPage.sidebarHeader)}</h5>
                {collection?.sidebar?.map(entry => {
                  const entryUrl = entry.store.slug.current;
                  let collectionUrl: string;
                  let collectionTitle = entry?.store?.title;

                  if (
                    entryUrl.startsWith('parts-accessories-') ||
                    entryUrl.startsWith('accessories-')
                  ) {
                    collectionUrl = `/accessories/${
                      entryUrl.split('accessories-')[1]
                    }`;
                  } else {
                    collectionUrl = `/${collection.store.slug.current}/${entryUrl}`;
                  }

                  if (
                    collection?.store?.slug?.current === 'accessories' &&
                    (entryUrl.startsWith('parts-accessories-') ||
                      entryUrl.startsWith('accessories-'))
                  ) {
                    collectionTitle = collectionTitle.replace(
                      tt(s => s.collectionsPage.collectionTitleReplaceValue),
                      ''
                    );
                  }

                  return (
                    <SidebarRow key={entry?.store?.title}>
                      <LocalizedLink to={collectionUrl}>
                        {collectionTitle} ({entry?.products?.length})
                      </LocalizedLink>
                    </SidebarRow>
                  );
                })}
              </>
            )}
          </Sidebar>

          <ContentContainer widthPercent={100 - sidebarWidthPercent}>
            {!collection?.hero?.mediaItem && !collection?.hero?.heroType && (
              <h1>{collection?.hero?.headline1}</h1>
            )}
            {collection?.bodyTop && (
              <StyledTopBodyContainer>
                <BlockBuilder
                  blocks={collection.bodyTop}
                  getInternalLink={getInternalLink}
                />
                {collection?.body && (
                  <a
                    href="#marketing-content"
                    onClick={() => {
                      marketingRef?.current?.scrollIntoView();
                    }}
                  >
                    {tt(s => s.collectionsPage.learnMoreLink)}
                  </a>
                )}
              </StyledTopBodyContainer>
            )}
            <CollectionHeader>
              <CollectionHeaderFilters name="product-filter">
                <FilterBox
                  onChange={navigateOnChange}
                  active={collection?.sidebar?.length > 0}
                >
                  <option>
                    {tt(s => s.collectionsPage.filterBoxGoToOption)}
                  </option>
                  {collection?.sidebar?.map(entry => (
                    <option
                      key={entry?.store?.title}
                      value={entry?.store?.slug?.current}
                    >
                      {entry?.store?.title}
                    </option>
                  ))}
                </FilterBox>
                <SortByBox>
                  <span>{tt(s => s.collectionsPage.sortByLabel)}</span>
                </SortByBox>
                <SelectSearch
                  onChange={handleSortingFilter}
                  value={filterValue}
                >
                  <option value="">
                    {tt(s => s.collectionsPage.featured)}
                  </option>
                  <option value="newest">
                    {tt(s => s.collectionsPage.newest)}
                  </option>
                  <option value="atoz">
                    {tt(s => s.collectionsPage.atoz)}
                  </option>
                  <option value="ztoa">
                    {tt(s => s.collectionsPage.ztoa)}
                  </option>
                  <option value="priceAsc">
                    {tt(s => s.collectionsPage.priceAsc)}
                  </option>
                  <option value="priceDesc">
                    {tt(s => s.collectionsPage.priceDesc)}
                  </option>
                </SelectSearch>
              </CollectionHeaderFilters>
            </CollectionHeader>

            {pagedProducts?.length > 0 && (
              <>
                <ProductCardGrid padding="0">
                  {pagedProducts.map(sanityProduct => {
                    const product = sanityProduct?.store;
                    return (
                      product && (
                        <ProductCard
                          key={product.title}
                          product={{
                            ...product,
                            image: {
                              src: getResizedProductImage(
                                product?.previewImageUrl
                              ),
                              altText: product.title,
                            },
                            sku: product.variants[0].sku,
                            price: product.variants[0].price,
                            features: sanityProduct?.features,
                          }}
                          showFeatureList={collection?.showFeatureList}
                          showInlineRatings={false}
                          categorySource={collection?.store?.title}
                        />
                      )
                    );
                  })}
                </ProductCardGrid>

                {(pageContext.previousPagePath || pageContext.nextPagePath) && (
                  <PaginationArea
                    previousPagePath={pageContext.previousPagePath}
                    nextPagePath={pageContext.nextPagePath}
                  />
                )}
              </>
            )}
            {collection?.additionalLinks?.length > 0 && (
              <AdditionalLinks
                links={collection.additionalLinks}
                getInternalLink={getInternalLink}
              />
            )}
            <div id="marketing-content" ref={marketingRef}>
              {collection?.body && (
                <BlockBuilder
                  blocks={collection.body}
                  getInternalLink={getInternalLink}
                />
              )}
            </div>
            {collection?.relatedArticles?.length > 0 && (
              <>
                <h2>{tt(s => s.collectionsPage.relatedArticlesHeader)}</h2>
                <BlockBuilderLinkGrid
                  value={{
                    items: collection.relatedArticles,
                  }}
                  getInternalLink={getInternalLink}
                />
              </>
            )}
          </ContentContainer>
        </Container>
      </ContainerStyles>
      <GearTagLine />
    </Layout>
  );
}

export const query = graphql`
  query SanityCollectionsQuery($collectionId: String, $locale: String) {
    collection: sanityGlobalProductCollection(_id: { eq: $collectionId }) {
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      hero {
        presentation
        heroType
        compactImage
        mediaItem {
          asset {
            _id
          }
          crop {
            bottom
            left
            right
            top
          }
          hotspot {
            x
            y
          }
          alt
        }
        externalVideo
        internalVideo {
          video: asset {
            url
          }
        }
        heroPoster {
          asset {
            url
          }
        }
        headline1
        headline2
        textAlignment
        enableTextShadow
        enableCta
        cta {
          linkType
          internalLink: _rawInternalLink(resolveReferences: { maxDepth: 2 })
          caption
          href
        }
        themeStyle
      }
      store: _rawStore(resolveReferences: { maxDepth: 3 })
      description
      image {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        crop: _rawCrop(resolveReferences: { maxDepth: 3 })
        hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
        alt
      }
      sidebar: localizedSidebarValue {
        products {
          _id
        }
        store {
          title
          slug {
            current
          }
        }
      }
      parentCollection {
        store {
          title
          slug {
            current
          }
        }
      }
      bodyTop: _rawLocalizedBodyTop(resolveReferences: { maxDepth: 5 })
      body: _rawLocalizedBody(resolveReferences: { maxDepth: 5 })
      products {
        _id
        features
        store {
          id
          title
          handle
          productType
          media {
            ... on SanityProductMediaImage {
              image {
                altText
                src
              }
            }
          }
          previewImageUrl
          tags
          priceRangeV2 {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants {
            sku
            price
            compareAtPrice
            previewImageUrl
            variantLifecycle
          }
          productLifecycle
        }
      }
      relatedArticles: localizedRelatedArticlesValue {
        id
        _key
        _type
        title
        slug {
          current
        }
        image: _rawImage(resolveReferences: { maxDepth: 3 })
      }
      additionalLinks {
        ... on SanityLink {
          _key
          _type
          caption
          blank
          href
          internalLink: _rawLocalizedInternalLink(
            resolveReferences: { maxDepth: 3 }
            language: $locale
          )
        }
      }
      showFeatureList
    }
  }
`;

export default CollectionTemplate;
